import React, { useState } from "react";
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from "../../components/CustomButtons/Button.jsx";
import { permsDict } from '../../shared/permsObj';

export const statusLabels = {
    'created': 'User Created',
    'modified': 'User Modified',
    'deleted': 'User Deleted',
    'permissions_modified': 'Permissions Modified'
};

const getPermName = (perm_id) => {
    const p = permsDict[perm_id];
    if (p) return `${p.main} - ${p.subgroup} - ${p.name}`;
    return `Permission #${perm_id}`;
};

const fieldNameOverrides = {
    lastname: 'Last Name',
    firstname: 'First Name',
    middlename: 'Middle Name',
    username: 'Username',
    email: 'Email',
    phone: 'Phone',
    street1: 'Street 1',
    street2: 'Street 2',
};

const formatFieldName = (key) => {
    const lower = key.toLowerCase();
    if (fieldNameOverrides[lower]) return fieldNameOverrides[lower];
    return key
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([a-zA-Z])(\d)/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

const UserHistoryViewDialog = (props) => {
    const { open, close, entry } = props;
    if (!entry) return null;
    let dataObj = {};
    try {
        dataObj = typeof entry.data === 'string' ? JSON.parse(entry.data) : entry.data;
    } catch(e) {
        dataObj = { raw: entry.data };
    }

    const [showAll, setShowAll] = useState(null);

    const isPermChange = entry.status === 'permissions_modified';
    const isModified = entry.status === 'modified';

    let changesObj = {};
    let allFields = dataObj;
    let hasChanges = false;

    if (isModified) {
        if (dataObj.changes && dataObj.all) {
            changesObj = dataObj.changes;
            allFields = dataObj.all;
            hasChanges = Object.keys(changesObj).length > 0;
        } else {
            const isChangeVal = (val) => val && typeof val === 'object' && val.hasOwnProperty('old') && val.hasOwnProperty('new');
            const detectedChanges = {};
            const flat = {};
            Object.keys(dataObj).forEach(key => {
                if (isChangeVal(dataObj[key])) {
                    detectedChanges[key] = dataObj[key];
                }
                flat[key] = dataObj[key];
            });
            if (Object.keys(detectedChanges).length > 0) {
                changesObj = detectedChanges;
                allFields = flat;
                hasChanges = true;
            }
        }
    }

    const renderValue = (val) => {
        if (val === null || val === undefined) return '';
        if (Array.isArray(val)) {
            if (val.length === 0) return '-';
            return val.map(v => typeof v === 'object' ? Object.entries(v).map(([k, v2]) => `${formatFieldName(k)}: ${v2 === null ? '-' : v2}`).join(', ') : String(v)).join('; ');
        }
        if (typeof val === 'object') {
            return Object.entries(val).map(([k, v2]) => `${formatFieldName(k)}: ${v2 === null ? '-' : v2}`).join(', ');
        }
        return String(val);
    };

    return (
        <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
            <DialogTitle>
                <div style={{ textAlign: 'center' }}>
                    {statusLabels[entry.status] || entry.status} - {entry.user_name || 'Unknown User'}
                </div>
            </DialogTitle>
            <div style={{ padding: 20 }}>
                <div style={{ marginBottom: 10 }}>
                    <strong>Date:</strong> {new Date(entry.timestamp).toLocaleString()}
                </div>
                <div style={{ marginBottom: 10 }}>
                    <strong>Submitter:</strong> {entry.submitter_name || 'N/A'}
                </div>
                <div style={{ marginBottom: 10 }}>
                    <strong>User:</strong> {entry.user_name || 'N/A'}
                </div>
                <div style={{ marginBottom: 10 }}>
                    <strong>Action:</strong> {statusLabels[entry.status] || entry.status}
                </div>
                {!isPermChange && <div style={{ marginBottom: 10 }}>
                    {hasChanges && <Button
                        color={(showAll || 'changes') === 'changes' ? 'primary' : 'white'}
                        size="sm"
                        onClick={() => setShowAll('changes')}
                        style={{ marginRight: 10 }}
                    >Changes Only</Button>}
                    <Button
                        color={(showAll || (hasChanges ? 'changes' : 'filled')) === 'filled' ? 'primary' : 'white'}
                        size="sm"
                        onClick={() => setShowAll('filled')}
                        style={{ marginRight: 10 }}
                    >Summary</Button>
                    <Button
                        color={(showAll || (hasChanges ? 'changes' : 'filled')) === 'all' ? 'primary' : 'white'}
                        size="sm"
                        onClick={() => setShowAll('all')}
                    >All Fields</Button>
                </div>}
                <hr />
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {isPermChange && (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f5f5f5' }}>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '15%' }}>Action</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '85%' }}>Permission</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(Array.isArray(dataObj.add) ? dataObj.add : []).map((item, i) => (
                                    <tr key={`add-${i}`}>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', color: '#2e7d32', fontWeight: 'bold' }}>Added</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>{item.perm_id ? getPermName(item.perm_id) : JSON.stringify(item)}</td>
                                    </tr>
                                ))}
                                {(Array.isArray(dataObj.remove) ? dataObj.remove : []).map((item, i) => (
                                    <tr key={`rm-${i}`}>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', color: '#e53935', fontWeight: 'bold' }}>Removed</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>{typeof item === 'object' ? (item.perm_id ? getPermName(item.perm_id) : JSON.stringify(item)) : `Permission Record #${item}`}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {!isPermChange && (showAll || (hasChanges ? 'changes' : 'filled')) === 'changes' && hasChanges && (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f5f5f5' }}>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '25%' }}>Field</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '37.5%' }}>Old Value</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '37.5%' }}>New Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(changesObj).map((key) => {
                                    const val = changesObj[key];
                                    return (
                                        <tr key={key}>
                                            <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>{formatFieldName(key)}</td>
                                            <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', color: '#e53935' }}>{renderValue(val.old)}</td>
                                            <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', color: '#2e7d32' }}>{renderValue(val.new)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                    {!isPermChange && (showAll || (hasChanges ? 'changes' : 'filled')) === 'filled' && (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f5f5f5' }}>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '30%' }}>Field</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '70%' }}>Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(allFields).filter((key) => {
                                    const val = allFields[key];
                                    if (val === null || val === undefined || val === '') return false;
                                    if (Array.isArray(val) && val.length === 0) return false;
                                    return true;
                                }).map((key) => (
                                    <tr key={key}>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>{formatFieldName(key)}</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>{renderValue(allFields[key])}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {!isPermChange && (showAll || (hasChanges ? 'changes' : 'filled')) === 'all' && (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f5f5f5' }}>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '30%' }}>Field</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '70%' }}>Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(allFields).map((key) => (
                                    <tr key={key}>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>{formatFieldName(key)}</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>{renderValue(allFields[key])}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <hr />
                <Button color="white" onClick={close}>CLOSE</Button>
            </div>
        </Dialog>
    );
};

export default UserHistoryViewDialog;
